import { computed } from "vue";
import { useResolvedFeatureFlag } from "@/lib/useFeatureFlag";

import { useBreakpoints } from "@/composables/useBreakpoints";

const FLAG = "mobile_web_optimization";

/**
 * The single gate for rendering a Seranova Drawer as a bottom sheet.
 *
 * Threshold is the Tailwind `md` breakpoint via `useBreakpoints`, matching the
 * reschedule notify toggle (AppointmentDetails/ScheduledTimeForm.vue). The two
 * appear over the same range, so a sheet never shows a toggle the surrounding
 * layout has already dropped.
 *
 * `flagsResolved` is the third state that `useFeatureFlag` cannot express, taken
 * from `useResolvedFeatureFlag`. It matters here because the disabled branch for
 * a calendar event is the jQuery/Bootstrap Modal, which imperatively shows
 * itself and registers a backdrop on mount, so mounting and discarding it
 * orphans that backdrop. Consumers whose two branches differ by more than a CSS
 * class must therefore render neither until `flagsResolved` is true.
 */
export function useSheetPlacement() {
  const { type } = useBreakpoints();
  const { enabled, resolved: flagsResolved } = useResolvedFeatureFlag(FLAG);

  const isSheet = computed(
    () => flagsResolved.value && enabled.value && type.value === "mobile"
  );

  const placement = computed<"right" | "bottom">(() =>
    isSheet.value ? "bottom" : "right"
  );

  return { isSheet, placement, flagsResolved };
}
